import {FormControl} from '@angular/forms';
import {PagedFilterDTO, PagedFilterDTONew} from '../models/PagedFilterDTO';
import {PagedRequestDTO} from '../models/PagedRequestDTO';
import {PagedResponseDTO} from '../models/PagedResponseDTO';

export class Common {
  public static readonly ONE_AND_HALF_SECOND_DEBOUNCE = 1500;
  public static readonly SHORT_DEBOUNCE_TIME = 200; // milliseconds
  public static readonly HALF_SECOND_DEBOUNCE = 500; // milliseconds
  public static readonly DEBOUNCE_TIME = 1000;
  public static readonly TWO_SECOND_DEBOUNCE = 2000; // milliseconds
  public static readonly EMPTY_VALUE = '';
  public static readonly MIME_TYPE_XLS = 'application/vnd.ms-excel';
  public static readonly MIME_TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  public static readonly MIME_TYPE_XLSM = 'application/vnd.ms-excel.sheet.macroEnabled.12';
}

export function createFieldMap(filterField: string, filterValue: string): {} {
  const pagedFilterDTO = new PagedFilterDTO();
  pagedFilterDTO.name = filterField;
  pagedFilterDTO.value = filterValue;

  const fieldMap = {};
  fieldMap[filterField] = pagedFilterDTO;

  return fieldMap;
}

export function createNewPagedFilterDTO(name: string, operator: string, value: string): PagedFilterDTONew {
  const pagedFilterDTO: PagedFilterDTONew = new PagedFilterDTONew();
  pagedFilterDTO.name = name;
  pagedFilterDTO.operator = operator;
  pagedFilterDTO.value = value;
  return pagedFilterDTO;
}

export function createPagedFilterDTO(name: string, value: string, operator: string = '=='): PagedFilterDTO {
  const pagedFilterDTO: PagedFilterDTO = new PagedFilterDTO();
  pagedFilterDTO.name = name;
  pagedFilterDTO.operator = operator;
  pagedFilterDTO.value = value;
  return pagedFilterDTO;
}

export function isNullOrEmpty(object): boolean {
  return !!object;
}

export function getSortDirection(directionPreAdjust: string): string {
  let sortDirection = '';

  if (directionPreAdjust === 'desc') {
    sortDirection = 'DESCENDING';
  }
  else if (directionPreAdjust === 'asc') {
    sortDirection = 'ASCENDING';
  }

  return sortDirection;
}

export function sortByPropertyAndDirection(property: string, direction: string) {
    const sortOrder = direction === 'asc' ? 1 : -1;
    const propertyArr = property.split('.');

    return (a, b) => {
        propertyArr.forEach(prop => {
            a = a[prop] ?? '';
            b = b[prop] ?? '';
        });

        const result = a < b ? -1 : (a > b ? 1 : 0);
        return result * sortOrder;
    };
}

export function getEndDateValueFromFormControl(endDate: FormControl) {
  return endDate.value != null && endDate.value !== '' ? new Date(endDate.value) : null;
}

export function setPotentialNullDateValueForFormControl(endDate: Date) {
  return endDate != null ? new Date(endDate) : null;
}

export function generateRandomString(length) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() *
      charactersLength));
  }
  return result;
}

export function deepCopy(data) {
  return JSON.parse(JSON.stringify(data));
}

export function formatNumber(value, fixedDigit = 2) {
  return Number(value).toFixed(fixedDigit);
}

export function includesAnyValueInList(listToCheck: string[], valueToMatch: string[]): boolean {
  return valueToMatch.some(value => {
    return listToCheck.includes(value);
  });
}

export function isValidDateFormat(input): boolean {
  const pattern = /^\d{4}\/\d{2}\/\d{2}$/; // yyyy/MM/dd
  return pattern.test(input);
}

export function isValidNumberWithTwoDecimalPlaces(input): boolean {
  if (isNaN(input)) {
    return false;
  }
  const decimalCount = (input.toString().split('.')[1] || '').length;
  return decimalCount === 2;
}

export function stringNullOrEmpty(input: string): boolean {
  return input == null || input === '';
}

export function getPastDateFromToday(days: number): Date {
  const today: Date = new Date();
  const pastDate: Date = new Date(today);
  pastDate.setDate(today.getDate() - days); // Subtract 'days' from today's date
  return pastDate;
}

export function getTitle(title: string): string {
  return `Greydom | ${title}`;
}

/**
 * Add field map filters for PagedRequestDTO
 * @param pagedRequestDTO
 * @param fieldMapItems - list of field map items
 * @param filterValue - value for the filter
 */

export function addFieldMapFiltersForPagedRequestDTO(pagedRequestDTO: PagedRequestDTO, fieldMapItems: string[], filterValue: string): PagedRequestDTO {
  for (let i = 0; i < fieldMapItems.length; i++) {
    const pagedFilterDTO: PagedFilterDTONew = new PagedFilterDTONew();
    pagedFilterDTO.name = fieldMapItems[i];
    pagedFilterDTO.value = filterValue.trim();
    pagedRequestDTO.fieldMap[fieldMapItems[i]] = pagedFilterDTO;
    if (i !== 0) {
      pagedFilterDTO.logicGate = 'OR';
    }
  }
  return pagedRequestDTO;
}

export type StatusColor = 'green' | 'blue' | 'red' | 'orange';

export function getStatusColor(status: string | number | null | undefined): StatusColor {
  if (status == null) {
    return 'orange';
  }

  if (typeof status === 'number') {
    return getStatusColorFromNumber(status);
  }

  return getStatusColorFromString(status);
}

export function getStatusColorFromString(status: string): StatusColor {
  switch (status.toLowerCase()) {
    case 'activated':
    case 'accepted':
    case 'approved':
    case 'success':
      return 'green';
    case 'partially accepted':
    case 'information':
    case 'setvalue':
      return 'blue';
    case 'suspended':
    case 'terminated':
    case 'ended':
    case 'rejected':
    case 'duplicate':
    case 'not taken up':
    case 'error':
    case 'reject':
      return 'red';
    case 'pend':
    case 'warn':
      return 'orange';
    default:
      return 'orange';
  }
}

export function getStatusColorFromNumber(status: number): StatusColor {
  switch (status) {
    case -1:
      return 'red';
    case 1:
      return 'green';
    default:
      return 'orange';
  }
}

export const MANY_RECORDS_LENGTH = 1000000;

export function getPaginatorLengthFromPagedResponse<T>(
  response: PagedResponseDTO<T>,
  pageNumber: number,
  pageSize: number
): number {
  if (response.morePages === true) {
    return MANY_RECORDS_LENGTH;
  }

  if (response.morePages === false) {
    return pageNumber * pageSize + (response.resultList?.length || 0);
  }

  return response.totalRecords;
}
